import { useAuth0 } from "@auth0/auth0-react";
import { auth, grpc, racehorse360 } from "@tsg/1st-grpc-web-rh360";
import React, { useContext, useMemo } from "react";
import { interceptErrors } from "./errors";

export const ApiClientContext = React.createContext<ApiClientContextInterface>(
  {} as any
);

interface GrpcClients {
  applicationFeatureFlagClient: racehorse360.ApplicationFeatureFlagServiceSimpleClient;
  facilityBlackoutClient: racehorse360.FacilityBlackoutServiceSimpleClient;
  facilityClient: racehorse360.FacilityServiceSimpleClient;
  horseClient: racehorse360.HorseServiceSimpleClient;
  horseExamClient: racehorse360.HorseExamServiceSimpleClient;
  horseExamRequirementClient: racehorse360.HorseExamRequirementServiceSimpleClient;
  notificationClient: racehorse360.NotificationServiceSimpleClient;
  raceConditionClient: racehorse360.RaceConditionServiceSimpleClient;
  raceClient: racehorse360.RaceServiceSimpleClient;
  regionClient: racehorse360.RegionServiceSimpleClient;
  rolePermissionClient: racehorse360.RolePermissionServiceSimpleClient;
  stallApplicationClient: racehorse360.StallApplicationServiceSimpleClient;
  trackOpticsVideoClient: racehorse360.TrackOpticsVideoServiceSimpleClient;
  trainerClient: racehorse360.TrainerServiceSimpleClient;
  upcomingRaceEntryClient: racehorse360.UpcomingRaceEntryServiceSimpleClient;
  userClient: racehorse360.UserServiceSimpleClient;
  vaccinationClient: racehorse360.VaccinationServiceSimpleClient;
  workOrderClient: racehorse360.WorkOrderServiceSimpleClient;
  workoutClient: racehorse360.WorkoutServiceSimpleClient;
  workoutRequestClient: racehorse360.WorkoutRequestServiceSimpleClient;
}

export interface ApiClientContextInterface extends GrpcClients {
  authClient: auth.UserProfileServiceSimpleClient;
  getAccessTokenSilently: () => Promise<string>;
}

export const useApiClient: () => ApiClientContextInterface = () =>
  useContext(ApiClientContext);

export const ApiClientProvider = ({
  children,
  isApiMocked = process.env.RACEHORSE360_API_MOCKED === "1",
  enableGrpcWebDevTools = Boolean(window.__GRPCWEB_DEVTOOLS__)
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const value = useMemo(() => {
    const clientOptions: grpc.CreateClientOptions = {
      auth: getAccessTokenSilently
    };

    const clients = Object.keys(racehorse360).reduce((acc, key) => {
      if (key.endsWith("ServiceSimpleClient")) {
        const client = grpc.createClient(
          racehorse360[key],
          racehorse360.packageName,
          clientOptions
        );
        interceptErrors(client);
        acc[
          key.charAt(0).toLowerCase() +
            key.substr(1).replace("ServiceSimple", "")
        ] = client;
      }
      return acc;
    }, {} as GrpcClients);

    const authClient = grpc.createClient(
      auth.UserProfileServiceSimpleClient,
      auth.packageName,
      clientOptions
    );
    interceptErrors(authClient);

    if (enableGrpcWebDevTools && !isApiMocked) {
      window.__GRPCWEB_DEVTOOLS__([...Object.values(clients), authClient]);
    }

    return {
      ...clients,
      authClient,
      getAccessTokenSilently
    };
  }, [isApiMocked, getAccessTokenSilently, enableGrpcWebDevTools]);

  return (
    <ApiClientContext.Provider value={value}>
      {children}
    </ApiClientContext.Provider>
  );
};
